import * as React from 'react';
import { useEffect } from 'react';
import { mapSiteDtoToModel, useCurrentSiteId } from '@hotjar/site';
import { navigateTo, navigateToOverview } from '@hotjar/webapp/utils/routing';
import { LOGIN } from '@hotjar/webapp/constants/routes/auth';
import { getLoggedIn, refreshFromServer } from '@hotjar/webapp/domains/common/user-data';
import { ONBOARDING } from '@hotjar/webapp/constants/routes/onboarding';
import { browserHistory } from '@hotjar/webapp-routing';
import { isDemoSiteId } from '@hotjar/webapp/domains/acquisition/demo/demo-site-redirections';
import { useQueryParameter } from '@hotjar/webapp/hooks/useQueryParameter';

export const ApplicationBaseRoute = (): JSX.Element => {
  const nextURL = useQueryParameter('next');
  const currentSiteId = useCurrentSiteId();
  const isLoggedIn = getLoggedIn();

  /**
   * A function which checks whether the user should be redirected to LMS or to the nextURL if available
   */
  const forwardToNextIfAvailable = () => {
    let cleanNext = '';
    if (nextURL) {
      cleanNext = nextURL?.startsWith('/') ? nextURL.slice(1) : nextURL;
      const search = new URLSearchParams(cleanNext);
      // If next was populated by LMS, forward to LMS immediately
      if (search.get('fromLMS') !== null) {
        // A login request was triggered from learning.hotjar.com
        let url = 'https://learning.hotjar.com';
        const returnURLParam = search.get('returnURL');
        if (returnURLParam !== null) url = returnURLParam;
        window.location.replace(url);
        return true;
      }
      if (nextURL === '/login') {
        // do not redirect
        return false;
      }
      // Otherwise, forward to nextURL
      browserHistory.replace(`${nextURL}`);
      return true;
    }
    return false;
  };

  /**
   * Holds checks for logged in or anonymous user redirections
   */
  useEffect(() => {
    if (isLoggedIn) {
      const nextAvailable = forwardToNextIfAvailable();
      if (!nextAvailable) {
        if (currentSiteId != null) {
          const isDemoSite = isDemoSiteId(currentSiteId);
          if (isDemoSite) {
            refreshFromServer().then((userData) => {
              const siteDTO = userData.sites.find(
                (site) => !isDemoSiteId(site.id) && Number(site.id) !== 14,
              );
              const site = siteDTO ? mapSiteDtoToModel(siteDTO) : undefined;
              navigateToOverview(String(site ? site.id : currentSiteId));
            });
          } else {
            navigateToOverview(String(currentSiteId));
          }
        } else {
          // Not sure about this line, but kept it as it was in app.js file
          navigateTo(ONBOARDING.path);
        }
      }
    } else {
      browserHistory.replace(LOGIN.path + window.location.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, currentSiteId]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <></>;
};
